/**
 * Platform slide-deck tools.
 *
 * Phase 13 P1 (June 2026)
 *
 * Three tools mounted on `scene_type=slide_deck`:
 *
 *   * `SlideDeckUploader`   — TOP_TOOLBAR; sets `deck_url` (PDF
 *                             upload) or wires an inline `slides[]`
 *                             collection.
 *   * `SlideThumbnailStrip` — BOTTOM_STRIP; renders the slide list
 *                             so the coach can re-order / select.
 *   * `SlideMCQMarker`      — RIGHT_INSP; per-slide MCQ marker
 *                             chooser (the audit suite enforces
 *                             that markers' `question_template_id`
 *                             resolves).
 */
import React from 'react';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { requiresSceneType } from '../predicates';

// ────────────────────────────────────────────────────────────────────
// SlideDeckUploader
// ────────────────────────────────────────────────────────────────────

function SlideDeckUploader() {
  const { scene, setDomainMetadata } = useDesigner();
  const [uploading, setUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadError, setUploadError] = React.useState(null);
  const fileInputRef = React.useRef(null);

  if (!scene) return null;
  const meta = scene.domain_metadata || {};
  const hasSlides = Array.isArray(meta.slides) && meta.slides.length > 0;
  const hasDeck = Boolean(meta.deck_id || meta.deck_url);

  const ACCEPTED_FORMATS = '.pptx,.ppt,.docx,.doc,.md';

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      const { slideDeckCoachAPI } = await import('../../services/api');

      const response = await slideDeckCoachAPI.uploadFile(file, {
        domainId: scene.domain_id,
        sceneId: scene.scene_id,
        title: scene.display_name || file.name,
        onProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(pct);
        },
      });

      const result = response?.data;

      if (result?.slides && Array.isArray(result.slides)) {
        setDomainMetadata({
          slides: result.slides,
          slide_mcq_markers: result.slide_mcq_markers || [],
          source_file: file.name,
          source_format: result.source_format,
          processing_notes: result.processing_notes,
        });
      } else if (result?.deck_id) {
        setDomainMetadata({
          deck_id: result.deck_id,
          deck_url: result.url || result.file_url,
          source_file: file.name,
        });
      }
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="designer-tool" data-tool-id="platform.slide_deck_uploader">
      <h3 className="designer-tool__heading">Deck Source</h3>

      {/* File upload */}
      <div className="designer-tool__row">
        <label className="designer-tool__label">
          Upload file (PPTX, DOCX, MD)
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FORMATS}
          onChange={handleFileUpload}
          disabled={uploading}
          style={{ fontSize: 13, marginBottom: 8 }}
        />
        {uploading && (
          <div style={{ fontSize: 12, color: '#60a5fa', marginTop: 4 }}>
            {uploadProgress < 100
              ? `Uploading... ${uploadProgress}%`
              : 'Processing with AI agent...'}
          </div>
        )}
        {uploadError && (
          <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>
            {uploadError}
          </div>
        )}
      </div>

      {/* URL input fallback */}
      <div className="designer-tool__row" style={{ marginTop: 8 }}>
        <label className="designer-tool__label">Or paste deck URL (PDF)</label>
        <input
          type="url"
          className="designer-tool__input"
          value={meta.deck_url || ''}
          aria-label="Slide deck URL"
          placeholder="https://cdn.example/deck.pdf"
          onChange={(e) => setDomainMetadata({ deck_url: e.target.value })}
        />
      </div>

      {/* Status */}
      {hasSlides && (
        <div style={{ fontSize: 12, color: '#22c55e', marginTop: 8 }}>
          ✓ {meta.slides.length} slides loaded
          {meta.source_file && ` (from ${meta.source_file})`}
        </div>
      )}
      {hasDeck && !hasSlides && (
        <div style={{ fontSize: 12, color: '#60a5fa', marginTop: 8 }}>
          ✓ PDF deck linked{meta.deck_id ? ` (${meta.deck_id})` : ''}
        </div>
      )}
    </div>
  );
}

export const slideDeckUploader = new DesignerTool({
  id:         'platform.slide_deck_uploader',
  label:      'Deck source',
  slot:       'RIGHT_INSP',
  category:   'metadata',
  applies_to: requiresSceneType('slide_deck'),
  Component:  SlideDeckUploader,
  weight:     20,
  helpText:   'Upload PPTX, DOCX, or MD files — AI processes them into structured slides. Or paste a PDF URL.',
});

// ────────────────────────────────────────────────────────────────────
// SlideThumbnailStrip
// ────────────────────────────────────────────────────────────────────

function SlideThumbnailStrip() {
  const { scene } = useDesigner();
  if (!scene) return null;
  const slides = (scene.domain_metadata?.slides) || [];

  return (
    <div
      className="designer-tool designer-tool--slide-strip"
      data-tool-id="platform.slide_thumbnail_strip"
      role="list"
      aria-label="Slide thumbnails"
    >
      {slides.length === 0 && (
        <span className="designer-tool__empty">No slides loaded yet.</span>
      )}
      {slides.map((s, i) => (
        <span
          key={s.slide_id || i}
          className="designer-tool__chip designer-tool__chip--slide"
          role="listitem"
        >
          #{i + 1}
        </span>
      ))}
    </div>
  );
}

export const slideThumbnailStrip = new DesignerTool({
  id:         'platform.slide_thumbnail_strip',
  label:      'Slide strip',
  slot:       'BOTTOM_STRIP',
  category:   'timeline',
  applies_to: requiresSceneType('slide_deck'),
  Component:  SlideThumbnailStrip,
  weight:     55,
  helpText:   'Re-order or select individual slides.',
});

// ────────────────────────────────────────────────────────────────────
// SlideMCQMarker
// ────────────────────────────────────────────────────────────────────

function SlideMCQMarker() {
  const { scene, setDomainMetadata } = useDesigner();
  const [editingIdx, setEditingIdx] = React.useState(null);
  if (!scene) return null;
  const markers = (scene.domain_metadata?.slide_mcq_markers) || [];
  const slideCount = (scene.domain_metadata?.slides || []).length;

  const updateMarkers = (next) => setDomainMetadata({ slide_mcq_markers: next });

  const addMarker = () => {
    const next = [
      ...markers,
      {
        marker_id: `sm_${markers.length + 1}_${Date.now().toString(36)}`,
        slide_index: 0,
        max_attempts: 2,
        question: {
          prompt: '',
          choices: [
            { choice_id: 'c1', text: '', correct: true },
            { choice_id: 'c2', text: '', correct: false },
          ],
          explanation: '',
        },
      },
    ];
    updateMarkers(next);
    setEditingIdx(next.length - 1);
  };

  const removeMarker = (idx) => {
    updateMarkers(markers.filter((_, i) => i !== idx));
    if (editingIdx === idx) setEditingIdx(null);
  };

  const updateMarker = (idx, patch) => {
    updateMarkers(markers.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };

  const updateQuestion = (idx, qPatch) => {
    const m = markers[idx];
    updateMarker(idx, { question: { ...(m.question || {}), ...qPatch } });
  };

  const addChoice = (idx) => {
    const q = markers[idx].question || {};
    const choices = q.choices || [];
    const id = `c${choices.length + 1}_${Date.now().toString(36)}`;
    updateQuestion(idx, { choices: [...choices, { choice_id: id, text: '', correct: false }] });
  };

  const updateChoice = (mIdx, cIdx, patch) => {
    const q = markers[mIdx].question || {};
    const choices = (q.choices || []).map((c, i) => (i === cIdx ? { ...c, ...patch } : c));
    updateQuestion(mIdx, { choices });
  };

  const setCorrect = (mIdx, choiceId) => {
    const q = markers[mIdx].question || {};
    const choices = (q.choices || []).map((c) => ({ ...c, correct: c.choice_id === choiceId }));
    updateQuestion(mIdx, { choices });
  };

  const removeChoice = (mIdx, cIdx) => {
    const q = markers[mIdx].question || {};
    updateQuestion(mIdx, { choices: (q.choices || []).filter((_, i) => i !== cIdx) });
  };

  return (
    <div
      className="designer-tool designer-tool--slide-markers"
      data-tool-id="platform.slide_mcq_marker"
    >
      <h3 className="designer-tool__heading">Slide Checkpoints</h3>
      <p style={{ fontSize: 11, color: 'var(--fg-text-muted)', marginBottom: 8 }}>
        Add questions to specific slides. The learner must answer correctly before navigating away.
      </p>

      {markers.length === 0 && (
        <span className="designer-tool__empty">No slide MCQs yet.</span>
      )}

      {markers.map((m, i) => {
        const q = m.question || {};
        const isEditing = editingIdx === i;

        return (
          <div key={m.marker_id || i} style={{
            border: '1px solid var(--border-default, #334155)',
            borderRadius: 8,
            padding: '8px 10px',
            marginBottom: 8,
            background: isEditing ? 'var(--bg-surface-secondary, rgba(59,130,246,0.05))' : 'transparent',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                <span style={{ fontSize: 12, color: 'var(--fg-text-muted)', fontWeight: 600, minWidth: 30 }}>
                  #{i + 1}
                </span>
                {slideCount > 0 ? (
                  <select
                    className="designer-tool__input"
                    value={m.slide_index}
                    aria-label={`Checkpoint ${i + 1} slide`}
                    style={{ width: 100, fontSize: 12 }}
                    onChange={(e) => updateMarker(i, { slide_index: parseInt(e.target.value) || 0 })}
                  >
                    {Array.from({ length: slideCount }, (_, si) => (
                      <option key={si} value={si}>Slide {si + 1}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="number"
                    min="1"
                    className="designer-tool__input"
                    value={m.slide_index + 1}
                    aria-label={`Checkpoint ${i + 1} slide number`}
                    style={{ width: 70, fontSize: 12 }}
                    onChange={(e) => updateMarker(i, { slide_index: Math.max(0, (parseInt(e.target.value) || 1) - 1) })}
                  />
                )}
                <span style={{ fontSize: 10, color: 'var(--fg-text-muted)' }}>
                  slide
                </span>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  type="button"
                  className="designer-tool__button designer-tool__button--icon"
                  onClick={() => setEditingIdx(isEditing ? null : i)}
                  title={isEditing ? 'Collapse' : 'Edit question'}
                >{isEditing ? '▲' : '▼'}</button>
                <button
                  type="button"
                  className="designer-tool__button designer-tool__button--icon"
                  onClick={() => removeMarker(i)}
                  title="Remove checkpoint"
                >×</button>
              </div>
            </div>

            {isEditing && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border-default, #334155)' }}>
                <div className="designer-tool__row">
                  <label className="designer-tool__label" style={{ fontSize: 11 }}>Question</label>
                  <textarea
                    className="designer-tool__input designer-tool__input--textarea"
                    rows={2}
                    value={q.prompt || ''}
                    placeholder="What did you just learn from this slide?"
                    onChange={(e) => updateQuestion(i, { prompt: e.target.value })}
                  />
                </div>

                <div style={{ marginTop: 6 }}>
                  <label className="designer-tool__label" style={{ fontSize: 10 }}>
                    Choices (select correct answer)
                  </label>
                  {(q.choices || []).map((c, ci) => (
                    <div key={c.choice_id || ci} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      <input
                        type="radio"
                        name={`slide-mcq-${m.marker_id}`}
                        checked={Boolean(c.correct)}
                        onChange={() => setCorrect(i, c.choice_id)}
                      />
                      <input
                        type="text"
                        className="designer-tool__input"
                        value={c.text || ''}
                        placeholder={`Choice ${ci + 1}`}
                        style={{ flex: 1, fontSize: 12 }}
                        onChange={(e) => updateChoice(i, ci, { text: e.target.value })}
                      />
                      <button
                        type="button"
                        className="designer-tool__button designer-tool__button--icon"
                        onClick={() => removeChoice(i, ci)}
                        style={{ fontSize: 10 }}
                      >×</button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="designer-tool__button designer-tool__button--small"
                    onClick={() => addChoice(i)}
                    style={{ fontSize: 11, marginTop: 4 }}
                  >+ Choice</button>
                </div>

                <div className="designer-tool__row" style={{ marginTop: 8 }}>
                  <label className="designer-tool__label" style={{ fontSize: 11 }}>Explanation (shown after answering)</label>
                  <textarea
                    className="designer-tool__input designer-tool__input--textarea"
                    rows={2}
                    value={q.explanation || ''}
                    placeholder="Optional explanation for the correct answer"
                    onChange={(e) => updateQuestion(i, { explanation: e.target.value })}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <label className="designer-tool__label" style={{ fontSize: 10, margin: 0 }}>Max attempts:</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    className="designer-tool__input"
                    value={m.max_attempts || 2}
                    style={{ width: 50, fontSize: 12 }}
                    onChange={(e) => updateMarker(i, { max_attempts: parseInt(e.target.value) || 2 })}
                  />
                </div>
              </div>
            )}

            {!isEditing && q.prompt && (
              <div style={{ fontSize: 11, color: 'var(--fg-text-muted)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {q.prompt}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        className="designer-tool__button"
        onClick={addMarker}
        style={{ marginTop: 4 }}
      >
        + Add Checkpoint
      </button>
    </div>
  );
}

export const slideMCQMarker = new DesignerTool({
  id:         'platform.slide_mcq_marker',
  label:      'Slide MCQs',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: requiresSceneType('slide_deck'),
  Component:  SlideMCQMarker,
  weight:     65,
  helpText:   'Attach multiple-choice questions to specific slides.',
});

export const slideTools = [slideDeckUploader, slideThumbnailStrip, slideMCQMarker];
export default slideTools;
